import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { LeadStatusPill } from 'app/features/leads/components/lead-status-pill';
import type { LeadSummary } from 'app/features/leads/services/gym-finder/types';
import Typography from 'app/ui/components/atoms/typography';
import { formatDateWithOrdinalMonthYear } from 'app/utils/date';

type LeadsTableProps = {
  leads: LeadSummary[];
  onViewDetails: (lead: LeadSummary) => void;
};

const TableContainer = styled.div`
  width: 100%;
  overflow: hidden;
  border-radius: ${({ theme: { spacing } }) => `${spacing(2)} ${spacing(2)} 0 0`};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  th {
    background: ${({ theme }) => theme.semantic.neutrals_200};
    color: ${({ theme }) => theme.semantic.text_small_elements};
    font-family: ${({ theme: { typography } }) => typography.poppins};
    font-weight: ${({ theme: { typography } }) => typography.light};
    font-size: ${({ theme: { typography } }) => typography.xsmall};
    text-align: left;
    text-transform: none;
    letter-spacing: normal;
    padding: ${({ theme: { spacing } }) => `${spacing(2)} ${spacing(3)}`};
    border-bottom: 0.0625rem solid ${({ theme }) => theme.semantic.neutrals_400};
    vertical-align: middle;
  }

  td {
    padding: ${({ theme: { spacing } }) => `${spacing(2)} ${spacing(3)}`};
    border-bottom: 0.0625rem solid ${({ theme }) => theme.semantic.neutrals_400};
    vertical-align: middle;
  }

  col:nth-child(5) {
    width: 7.5rem;
  }
`;

const DataRow = styled.tr<{ $isHighlighted?: boolean }>`
  background: ${({ theme, $isHighlighted }) => ($isHighlighted ? theme.semantic.neutrals_100 : 'transparent')};
`;

const AthleteCell = styled.td<{ $isHighlighted?: boolean }>`
  border-left: ${({ theme, $isHighlighted }) =>
    $isHighlighted ? `0.0625rem solid ${theme.semantic.accent_500}` : 'transparent'};
`;

const AthleteIdentity = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(2)};
`;

const AthleteEmail = styled.span`
  font-family: ${({ theme: { typography } }) => typography.poppins};
  font-weight: ${({ theme: { typography } }) => typography.light};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.semantic.neutrals_600};
  line-height: ${({ theme: { typography } }) => typography.lineHeight};
`;

const CellText = styled(Typography)``;

const ActionsCell = styled.td`
  text-align: right;
  white-space: nowrap;
`;

const ViewDetailsButton = styled.button`
  cursor: pointer;
  background: transparent;
  border: none;
  padding: 0;
  text-decoration: none;
  color: ${({ theme }) => theme.semantic.accent_500};
  font-family: ${({ theme: { typography } }) => typography.poppins};
  font-size: ${({ theme: { typography } }) => typography.xsmall};
  font-weight: ${({ theme: { typography } }) => typography.light};

  &:hover {
    color: ${({ theme }) => theme.semantic.accent_700};
  }

  &:focus-visible {
    outline: ${({ theme: { spacing } }) => spacing(0.5)} solid ${({ theme }) => theme.semantic.accent_500};
    outline-offset: ${({ theme: { spacing } }) => spacing(0.5)};
  }
`;

const EmptyRow = styled.tr`
  td {
    text-align: center;
    color: ${({ theme }) => theme.semantic.neutrals_600};
  }
`;

export const LeadsTable = ({ leads, onViewDetails }: LeadsTableProps) => {
  const intl = useIntl();

  const headers = {
    athlete: intl.formatMessage({ id: 'leads.table.athlete' }),
    interestedIn: intl.formatMessage({ id: 'leads.table.interestedIn' }),
    receivedAt: intl.formatMessage({ id: 'leads.table.receivedAt' }),
    status: intl.formatMessage({ id: 'leads.table.status' }),
    actions: intl.formatMessage({ id: 'leads.table.actions' }),
  };

  return (
    <TableContainer>
      <Table aria-label={intl.formatMessage({ id: 'leads.table.ariaLabel' })} data-testid="leads-table">
        <colgroup>
          <col />
          <col />
          <col />
          <col />
          <col />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">{headers.athlete}</th>
            <th scope="col">{headers.interestedIn}</th>
            <th scope="col">{headers.receivedAt}</th>
            <th scope="col">{headers.status}</th>
            <th scope="col">
              <span className="visually-hidden">{headers.actions}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {leads.length === 0 ? (
            <EmptyRow data-testid="leads-table-empty">
              <td colSpan={5}>
                <Typography variant="body2">{intl.formatMessage({ id: 'leads.table.empty' })}</Typography>
              </td>
            </EmptyRow>
          ) : (
            leads.map((lead, index) => (
              <DataRow key={lead.id} data-testid={`leads-row-${lead.id}`} $isHighlighted={index === 0}>
                <AthleteCell $isHighlighted={index === 0}>
                  <AthleteIdentity>
                    <CellText variant="body3">{lead.athleteName}</CellText>
                    <AthleteEmail>{lead.athleteEmail}</AthleteEmail>
                  </AthleteIdentity>
                </AthleteCell>
                <td>
                  <CellText variant="body3">{lead.enquiryType}</CellText>
                </td>
                <td>
                  <CellText variant="body3">{formatDateWithOrdinalMonthYear(lead.receivedAt, intl.locale)}</CellText>
                </td>
                <td>
                  <LeadStatusPill status={lead.status} />
                </td>
                <ActionsCell>
                  <ViewDetailsButton
                    type="button"
                    aria-label={intl.formatMessage(
                      { id: 'leads.table.viewDetailsFor' },
                      { athleteName: lead.athleteName },
                    )}
                    data-testid={`leads-view-details-${lead.id}`}
                    onClick={() => onViewDetails(lead)}
                  >
                    {intl.formatMessage({ id: 'leads.table.viewDetails' })}
                  </ViewDetailsButton>
                </ActionsCell>
              </DataRow>
            ))
          )}
        </tbody>
      </Table>
    </TableContainer>
  );
};

export default LeadsTable;
